import { Component, inject, Input } from '@angular/core';
import { AbregelungKopplungsTyp } from '../../../../domain/model/abregelung-kopplungs-typ.enum';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FormGroupConfig } from '../../form-config.model';
import { AbregelungsinformationenIec101104 } from '../../../../domain/model/abregelungsinformationen-iec101104.model';
import { MatValidators } from '../../../../common/material/components/mat-form-field-required.directive';
import { ErzeugungsanlageConfig } from '../../erzeugungsanlage.config';
import {
    AbregelinformationenIec101104AdresseComponent,
} from './abregelinformationen-iec101104-adresse/abregelinformationen-iec101104-adresse.component';
import {
    AbregelungsinformationenIec101104Adressen,
} from '../../../../domain/model/abregelungsinformationen-iec101104-adressen.model';
import { Feld } from '../../../../domain/model/feld.enum';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeldBeschreibungService } from '../../../../service/feldbeschreibung/feldbeschreibung.service';
import { AbregelungInformationen } from '../abregelung-informationen';

@Component({
    selector: 'redispatch-abregelinformationen-iec101104-detail',
    templateUrl: './abregelinformationen-iec101104-detail.component.html',
    styleUrls: [
        './abregelinformationen-iec101104-detail.component.scss',
    ],
    standalone: false,
})
export class AbregelinformationenIec101104DetailComponent
    extends AbregelungInformationen<AbregelungsinformationenIec101104, AbregelungsinformationenIec101104Adressen> {

    public readonly LYT_GAP = ErzeugungsanlageConfig.HORIZONTAL_FX_LAYOUT_GAP;
    public readonly V_LYT_GAP = ErzeugungsanlageConfig.VERTICAL_FX_LAYOUT_GAP;
    public readonly COLS = ErzeugungsanlageConfig.ABREGELINFOS_ADDR_IEC_FX_FLEX_COL;
    public readonly Feld = Feld;

    @Input({ required: true }) disabled: boolean;
    @Input({ required: true }) readonly: boolean;

    private feldBeschreibungService = inject(FeldBeschreibungService);

    constructor() {
        super(inject(UntypedFormBuilder));
    }

    get formGroupConfig(): FormGroupConfig<AbregelungsinformationenIec101104> {
        return {
            gemeinsameASDUAdresse: [
                { value: null, disabled: this.disabled || this.readonly },
                [
                    MatValidators.required,
                    Validators.min(1),
                    Validators.max(65534),
                ],
            ],
            adressen: this.fb.array([
                this.createAddressFormGroup(AbregelungKopplungsTyp.SOLLWERT, true),
                this.createAddressFormGroup(AbregelungKopplungsTyp.TYP_100, true),
                this.createAddressFormGroup(AbregelungKopplungsTyp.TYP_60, true),
                this.createAddressFormGroup(AbregelungKopplungsTyp.TYP_30, true),
                this.createAddressFormGroup(AbregelungKopplungsTyp.TYP_0, true),
                this.createAddressFormGroup(AbregelungKopplungsTyp.WIRKLEISTUNG_P, false),
                this.createAddressFormGroup(AbregelungKopplungsTyp.BLINDLEISTUNG_Q, false),
            ]),
        };
    }

    public get fAdressen(): UntypedFormGroup[] {
        return (this.f.adressen as UntypedFormArray).controls as UntypedFormGroup[];
    }

    public sanitize(infos?: AbregelungsinformationenIec101104): AbregelungsinformationenIec101104 | undefined {
        if (infos?.adressen) {
            infos.adressen = infos.adressen.filter(value => this.hasAddressValue(value));
        }
        return infos;
    }

    public getHilfetext(feld: Feld): Observable<string | null> {
        return this.feldBeschreibungService.getCachedFeldBeschreibung(feld).pipe(
            map(value => value?.hilfetext),
        );
    }

    protected hasAddressValue(address: AbregelungsinformationenIec101104Adressen): boolean {
        return [
            address.hohesByte,
            address.mittleresByte,
            address.niedrigesByte,
            address.typkennung,
        ].every(v => typeof v === 'number');
    }

    private createAddressFormGroup(typ: AbregelungKopplungsTyp, required: boolean): UntypedFormGroup {
        const form = this.fb.nonNullable.group(
            AbregelinformationenIec101104AdresseComponent.formGroupConfig(typ, this.readonly || this.disabled || this.isHidden(typ), required),
            AbregelinformationenIec101104AdresseComponent.formOptions(required),
        );
        (form as any).customPassthroughRequired = required;
        return form;
    }
}
